import {
  type ComputedRef,
  computed,
  onBeforeUnmount,
  onErrorCaptured,
  onMounted,
  type ShallowRef,
  shallowRef,
  watch,
} from 'vue';
import type { PriceConfig } from '@/domain/types';
import {
  auditKioskRuntimeStartupConfig,
  resolveKioskPriceConfig,
} from '@/kiosk-core';
import {
  deploymentVersion,
  reloadWithCacheBust,
} from '@/services/deploymentVersion';
import { getKioskSlug } from '@/services/urlState';
import {
  addBreadcrumb,
  captureException,
  endSentrySession,
  flushSentry,
} from '@/telemetry/sentry';
import { resolveKioskPaymentReaderId } from '@/transport/kioskPaymentTransport';
import { resolveKioskPrintConfig } from '@/transport/kioskPrintTransport';
import { MOCK_PRICES } from '@/transport/mockData';
import { installUnloadGuards } from './bootHardening';
import { createEntrRuntimeFromConfig } from './createEntrRuntimeFromConfig';
import { useEntrAppController } from './useEntrAppController';
import { useKioskConfig } from './useKioskConfig';

interface UseEntrBootOptions {
  onAttractReset?: () => void;
}

interface UseEntrBootResult {
  controller: ReturnType<typeof useEntrAppController>;
  hasFatalRenderError: Readonly<ShallowRef<boolean>>;
  priceConfig: ComputedRef<PriceConfig>;
  reloadApp: () => Promise<void>;
}

export function useEntrBoot(
  options: UseEntrBootOptions = {}
): UseEntrBootResult {
  const kioskConfig = useKioskConfig();
  const runtime = createEntrRuntimeFromConfig(kioskConfig);
  const priceConfigResult = computed(() =>
    resolveKioskPriceConfig({
      strict: shouldUseStrictRuntimeConfig(),
      currency: kioskConfig.currency.value,
      portraitPriceMajor: kioskConfig.pricePerPhoto.value,
      groupPhotoPriceMajor: kioskConfig.groupPhotoPrice.value,
      fallback: MOCK_PRICES,
    })
  );
  const priceConfig = computed<PriceConfig>(() =>
    priceConfigResult.value.ok ? priceConfigResult.value.value : MOCK_PRICES
  );
  const isRuntimePaymentEnabled = computed(
    () =>
      import.meta.env.VITE_ENTR_PAYMENT_ENABLED !== 'false' &&
      (!shouldUseStrictRuntimeConfig() ||
        kioskConfig.terminalPaymentEnabled.value === true)
  );
  const controller = useEntrAppController({
    runtime,
    onAttractReset: options.onAttractReset,
    priceConfig: () => priceConfig.value,
    paymentEnabled: () => isRuntimePaymentEnabled.value,
  });
  const hasFatalRenderError = shallowRef(false);
  const hasActiveFlow = computed(
    () =>
      Boolean(controller.session.value) ||
      controller.paymentState.value === 'waiting' ||
      controller.printState.value === 'printing'
  );
  const unloadGuards = installUnloadGuards({
    hasActiveFlow: () => hasActiveFlow.value,
  });
  let fatalReloadTimer: number | null = null;

  onMounted(() => {
    void startEntrApp();
    void deploymentVersion.initialize({
      hasActiveSession: () => hasActiveFlow.value,
    });
  });

  onBeforeUnmount(() => {
    unloadGuards.uninstall();
    deploymentVersion.destroy();
    if (fatalReloadTimer !== null) {
      window.clearTimeout(fatalReloadTimer);
      fatalReloadTimer = null;
    }
  });

  watch(hasActiveFlow, (active) => {
    if (active || !deploymentVersion.hasPendingUpdate) return;
    deploymentVersion.reloadPendingUpdate();
  });

  onErrorCaptured((error, _instance, info) => {
    addBreadcrumb('entr.boot', 'Vue render error captured', { info });
    captureException(error, 'vue_render_error');
    endSentrySession('abnormal');
    hasFatalRenderError.value = true;
    scheduleFatalReload();
    return false;
  });

  function scheduleFatalReload(): void {
    if (fatalReloadTimer !== null) return;
    fatalReloadTimer = window.setTimeout(() => {
      void reloadApp();
    }, 5000);
  }

  async function reloadApp(): Promise<void> {
    await flushSentry(1500);
    reloadWithCacheBust();
  }

  async function startEntrApp(): Promise<void> {
    try {
      if (shouldLoadKioskConfig()) {
        await kioskConfig.load(getKioskSlug() ?? undefined);
      }
      assertRuntimeStartupConfig();
      await controller.start();
    } catch (error) {
      captureException(error, 'entr.startup');
      hasFatalRenderError.value = true;
      scheduleFatalReload();
    }
  }

  function assertRuntimeStartupConfig(): void {
    const printer = resolveKioskPrintConfig({
      resolvePrinterId: () => kioskConfig.printerName.value,
      resolvePrinterConfig: () =>
        kioskConfig.isReady.value ? kioskConfig.printerConfig.value : undefined,
    });
    const audit = auditKioskRuntimeStartupConfig({
      strict: shouldUseStrictRuntimeConfig(),
      priceConfig: priceConfigResult.value,
      printTransport: import.meta.env.VITE_ENTR_PRINT_TRANSPORT,
      printer,
      payment: {
        terminalPaymentEnabled: isRuntimePaymentEnabled.value,
        readerId: resolveKioskPaymentReaderId(),
        paymentDestination: kioskConfig.paymentDestination.value,
        stripeAccountId: kioskConfig.stripeAccountId.value,
      },
    });
    if (audit.ok) return;
    throw new Error(`entr_startup_config_invalid: ${audit.errors.join('; ')}`);
  }

  return {
    controller,
    hasFatalRenderError,
    priceConfig,
    reloadApp,
  };
}

function shouldLoadKioskConfig(): boolean {
  return (
    import.meta.env.PROD || import.meta.env.VITE_ENTR_TRANSPORT === 'backend'
  );
}

function shouldUseStrictRuntimeConfig(): boolean {
  return shouldLoadKioskConfig();
}
